import { DynamicValuesEnum } from "src/core/enums/dynamic-values.enum";


/**
 * Calculate scaled node size so it looks the same
 * when zoom level changes.
 *
 * @param baseSize original node size (default 20px)
 * @param zoom current zoom level (1 → 10)
 * @returns scaled size in px (number)
 */
export const getScaledNodeSize = (baseSize: number, zoom: number): number => {
  if (zoom <= 0) return baseSize;
  return baseSize * (1 / zoom);
};

/**
 * Calculate position offset for circles based on dynamic size
 * @param dynamicCircle Current dynamic circle size
 * @returns Position offset to center the circle
 */
export const calculateCirclePositionOffset = (
  dynamicCircle: number,
  overrideDefaultSizeValue?: number
): number => {
  return ((DynamicValuesEnum.CIRCLE + (overrideDefaultSizeValue || 0)) - dynamicCircle) / 2;
};

/**
 * Calculate position offset for gateways based on dynamic size
 * @param dynamicGateway Current dynamic gateway size
 * @returns Position offset to center the gateway
 */
export const calculateGatewayPositionOffset = (
  dynamicGateway: number
): number => {
  return (DynamicValuesEnum.GATEWAY_SIZE - dynamicGateway) / 2;
};

/**
 * Calculate position offset for photocells based on dynamic size
 * @param dynamicPhotocell Current dynamic photocell size
 * @returns Position offset to center the photocell
 */
export const calculatePhotocellPositionOffset = (
  dynamicPhotocell: number
): number => {
  return (DynamicValuesEnum.PHOTOCELL_SIZE - dynamicPhotocell) / 2;
};

/**
 * Interface for all dynamic values used in the floor plan component
 */
export interface DynamicValues {
  circle: number;
  circlePositionOffset: number;
  labelDynamics: {
    fontSize: number;
    height: number;
    yOffset: number;
    padding: number;
    textPadding: number;
    strokeWidth: number;
    cornerRadius: number;
    shadowBlur: number;
    shadowColor: number;
    shadowOffset: number;
  };
  gateway: {
    size: number;
    offset: number;
    labelOffset: number;
  };
  photocell: {
    size: number;
    offset: number;
    labelOffset: number;
  };
}

/** 
Example usages

* Default behavior (no overrides):
    calculateAllDynamicValues(1.5)

*Override only one top-level key:
  calculateAllDynamicValues(1.2, { circle: 30 })

*Override nested keys:
  calculateAllDynamicValues(1, { labelDynamics: { fontSize: 14, padding: 4 } })

**/

/**
 * Calculate all dynamic values for the given zoom level
 * @param zoomLevel Current zoom level
 * @returns Object containing all calculated dynamic values
 */
export const calculateAllDynamicValues = (
  zoomLevel: number,
  overrideDefaultSizeValues?: Partial<DynamicValues>
): DynamicValues => {
  // Calculate all dynamic values using the enum parameters
  const dynamicCircleValue = getScaledNodeSize(
    DynamicValuesEnum.CIRCLE + (overrideDefaultSizeValues?.circle || 0),
    zoomLevel
  );
  const dynamicFontSizeValue = getScaledNodeSize(
    DynamicValuesEnum.FONT_SIZE + (overrideDefaultSizeValues?.labelDynamics?.fontSize || 0),
    zoomLevel
  );
  const dynamicLabelHeightValue = getScaledNodeSize(
    DynamicValuesEnum.LABEL_HEIGHT + (overrideDefaultSizeValues?.labelDynamics?.height || 0),
    zoomLevel
  );
  const dynamicLabelYOffsetValue = getScaledNodeSize(
    DynamicValuesEnum.LABEL_Y_OFFSET + (overrideDefaultSizeValues?.labelDynamics?.yOffset || 0),
    zoomLevel
  );
  const dynamicLabelPaddingValue = getScaledNodeSize(
    DynamicValuesEnum.LABEL_PADDING + (overrideDefaultSizeValues?.labelDynamics?.padding || 0),
    zoomLevel
  );
  const dynamicTextPaddingValue = getScaledNodeSize(
    DynamicValuesEnum.TEXT_PADDING + (overrideDefaultSizeValues?.labelDynamics?.textPadding || 0),
    zoomLevel
  );
  const dynamicStrokeWidthValue = getScaledNodeSize(
    DynamicValuesEnum.STROKE_WIDTH + (overrideDefaultSizeValues?.labelDynamics?.strokeWidth || 0),
    zoomLevel
  );
  const dynamicCornerRadiusValue = getScaledNodeSize(
    DynamicValuesEnum.CORNER_RADIUS + (overrideDefaultSizeValues?.labelDynamics?.cornerRadius || 0),
    zoomLevel
  );
  const dynamicShadowBlurValue = getScaledNodeSize(
    DynamicValuesEnum.SHADOW_BLUR + (overrideDefaultSizeValues?.labelDynamics?.shadowBlur || 0),
    zoomLevel
  );
  const dynamicShadowColorValue = getScaledNodeSize(
    DynamicValuesEnum.SHADOW_COLOR + (overrideDefaultSizeValues?.labelDynamics?.shadowColor || 0),
    zoomLevel
  );
  const dynamicShadowOffsetValue = getScaledNodeSize(
    DynamicValuesEnum.SHADOW_OFFSET + (overrideDefaultSizeValues?.labelDynamics?.shadowOffset || 0),
    zoomLevel
  );

  const dynamicGatewayValue = getScaledNodeSize(
    DynamicValuesEnum.GATEWAY_SIZE + (overrideDefaultSizeValues?.gateway?.size || 0),
    zoomLevel
  );

  const dynamicPhotocellValue = getScaledNodeSize(
    DynamicValuesEnum.PHOTOCELL_SIZE + (overrideDefaultSizeValues?.photocell?.size || 0),
    zoomLevel
  );

  const dynamicGatewayLabelOffsetValue =
    DynamicValuesEnum.GATEWAY_LABEL_OFFSET * (1 - 1 / zoomLevel);
  const dynamicPhotocellLabelOffsetValue =
    DynamicValuesEnum.PHOTOCELL_LABEL_OFFSET * (1 - 1 / zoomLevel);
  const gatewayOffsetValue = calculateGatewayPositionOffset(dynamicGatewayValue);
  const photocellOffsetValue = calculatePhotocellPositionOffset(dynamicPhotocellValue);

  return {
    circle: dynamicCircleValue,
    circlePositionOffset: calculateCirclePositionOffset(dynamicCircleValue, overrideDefaultSizeValues?.circle),
    labelDynamics: {
    fontSize: dynamicFontSizeValue,
    height: dynamicLabelHeightValue,
    yOffset: dynamicLabelYOffsetValue,
    padding: dynamicLabelPaddingValue,
    textPadding: dynamicTextPaddingValue,
    strokeWidth: dynamicStrokeWidthValue,
    cornerRadius: dynamicCornerRadiusValue,
    shadowBlur: dynamicShadowBlurValue,
    shadowColor: dynamicShadowColorValue,
    shadowOffset: dynamicShadowOffsetValue,
    },
    gateway: {
    size: dynamicGatewayValue,
    offset: gatewayOffsetValue,
    labelOffset: dynamicGatewayLabelOffsetValue,
    },
    photocell: {
    size: dynamicPhotocellValue,
    offset: photocellOffsetValue,
    labelOffset: dynamicPhotocellLabelOffsetValue,
    },
  };
};
